import type { Profile } from '@tunasong/schemas'
import cn from 'classnames'
import type { FC, HTMLAttributes } from 'react'
import { ProfileAvatarImage } from '../profile/profile-avatar-image.js'
import styles from './avatar-stack.module.css'

/** Diameter of each avatar in px */
const avatarPx = 24

export interface AvatarStackProps extends HTMLAttributes<HTMLSpanElement> {
  /**
   * The people, in display order. A profile may carry only a `userId` (still loading, or not readable) or only an
   * `email` (a guest); each still takes a slot so the count and the width are right from the first render.
   */
  members: Partial<Profile>[]
  /** Avatars shown before "+N". @default 3 */
  max?: number
}

/**
 * Overlapping member avatars, then "+N". Presentational: no data loading and nothing focusable. It is one image
 * to assistive technology ("4 members"); the avatars inside are hidden from it.
 */
export const AvatarStack: FC<AvatarStackProps> = props => {
  const { members, max = 3, className, ...restProps } = props
  const shown = members.slice(0, max)
  const rest = members.length - shown.length
  const label = `${members.length} ${members.length === 1 ? 'member' : 'members'}`

  return (
    <span {...restProps} role="img" aria-label={label} className={cn(styles.stack, className)}>
      {shown.map((member, index) => (
        <span key={member.userId ?? member.email ?? index} aria-hidden="true" className={styles.item} data-avatar>
          <ProfileAvatarImage profile={member} px={avatarPx} alt="" />
        </span>
      ))}
      {rest > 0 ? (
        <span aria-hidden="true" className={styles.more}>
          +{rest}
        </span>
      ) : null}
    </span>
  )
}

export default AvatarStack
